// Legacy standalone cartage billing — turn completed trucking jobs (loads/tonnes/hours) into a
// customer invoice at your cartage rates. Uses the normal customer-invoice
// endpoint (numbering, GST, payment terms all standard) then marks the
// trucking jobs invoiced so they can't be billed twice.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const UNITS = [
  { key: "tonnes", label: "$/tonne" },
  { key: "loads", label: "$/load" },
  { key: "hours", label: "$/hour" },
];

function qtyFor(job, unit) {
  if (unit === "tonnes") return (job.actual_tonnes ?? job.tonnes_done) || 0;
  if (unit === "loads") return (job.actual_loads ?? job.loads_done) || 0;
  return job.hours_logged || 0;
}

function defaultUnit(job) {
  if ((job.actual_tonnes ?? job.tonnes_done) > 0) return "tonnes";
  if ((job.actual_loads ?? job.loads_done) > 0) return "loads";
  return "hours";
}

export function BillCartageDialog({ date, clients, onClose, onBilled }) {
  const [from, setFrom] = useState(date);
  const [to, setTo] = useState(date);
  const [jobs, setJobs] = useState(null);
  const [rates, setRates] = useState({});
  // per-job billing config: {jobId: {selected, unit, qty, rate}}
  const [cfg, setCfg] = useState({});
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setJobs(null);
    api.get("/trucking/billable", { params: { date_from: from, date_to: to } })
      .then(({ data }) => {
        setJobs(data.items || []);
        setRates(data.rates || {});
        const next = {};
        for (const j of data.items || []) {
          const unit = defaultUnit(j);
          next[j.id] = {
            selected: true,
            unit,
            qty: qtyFor(j, unit),
            rate: data.rates?.[`${(j.material || "").trim().toLowerCase()}|${unit}`] ?? "",
          };
        }
        setCfg(next);
        const clientIds = [...new Set((data.items || []).map((j) => j.client_id).filter(Boolean))];
        if (clientIds.length === 1) setClientId(clientIds[0]);
      })
      .catch(() => setJobs([]));
  }, [from, to]);

  const setJob = (id, patch) => setCfg((c) => ({ ...c, [id]: { ...c[id], ...patch } }));

  const selected = (jobs || []).filter((j) => cfg[j.id]?.selected);
  const lineTotal = (j) => (Number(cfg[j.id]?.qty) || 0) * (Number(cfg[j.id]?.rate) || 0);
  const subtotal = selected.reduce((s, j) => s + lineTotal(j), 0);
  const client = clients.find((c) => c.id === clientId);

  const create = async () => {
    if (!client) return toast.error("Pick the customer to invoice");
    if (!selected.length) return toast.error("Tick at least one trucking job");
    for (const j of selected) {
      if (!(Number(cfg[j.id]?.rate) > 0)) return toast.error(`Enter a rate for "${j.material || "job"}" (${j.date})`);
      if (!(Number(cfg[j.id]?.qty) > 0)) return toast.error(`Quantity is 0 for "${j.material || "job"}" (${j.date})`);
    }
    setBusy(true);
    try {
      const line_items = selected.map((j) => {
        const c = cfg[j.id];
        const leg = [j.pickup_location, j.dropoff_location].filter(Boolean).join(" → ");
        const unitWord = c.unit === "tonnes" ? "t" : c.unit === "loads" ? "loads" : "hrs";
        const nDockets = (j.dockets || []).length;
        return {
          description: `Cartage — ${j.material || "general"}${leg ? ` · ${leg}` : ""} (${j.date}, ${c.qty} ${unitWord}${nDockets ? `, ${nDockets} docket${nDockets > 1 ? "s" : ""} on file` : ""})`,
          qty: Number(c.qty),
          unit_price: Number(c.rate),
        };
      });
      const { data: inv } = await api.post("/customer-invoices", {
        client_id: client.id,
        client_name: client.name,
        client_email: client.email || "",
        client_address: client.address || "",
        issue_date: new Date().toISOString().slice(0, 10),
        line_items,
        notes: `Cartage ${from === to ? from : `${from} → ${to}`}`,
        gst_inclusive: false,
      });
      await api.post("/trucking/mark-invoiced", {
        job_ids: selected.map((j) => j.id),
        invoice_id: inv.id,
        invoice_number: inv.invoice_number,
        rates: selected.map((j) => ({ material: j.material, unit: cfg[j.id].unit, rate: Number(cfg[j.id].rate) })),
      });
      toast.success(`Invoice ${inv.invoice_number} created — ${money(inv.total)} incl. GST`);
      onBilled?.(inv);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="bill-cartage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#FF5F15]" /> Legacy Bill Cartage
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500">Standalone compatibility billing. Prefilled rates are remembered suggestions; confirm the customer sell rate before invoicing. Master trucking jobs use Ready to Bill.</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <Label className="label-tag">From</Label>
            <Input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} className="mt-1.5 rounded-sm border-zinc-400" data-testid="bill-cartage-from" />
          </div>
          <div>
            <Label className="label-tag">To</Label>
            <Input type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} className="mt-1.5 rounded-sm border-zinc-400" data-testid="bill-cartage-to" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Label className="label-tag">Customer</Label>
            <Select value={clientId || "_none"} onValueChange={(v) => setClientId(v === "_none" ? "" : v)}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="bill-cartage-client"><SelectValue placeholder="Pick customer…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Pick customer —</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {jobs === null ? (
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 py-6 text-center">Loading completed jobs…</div>
        ) : jobs.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500" data-testid="bill-cartage-empty">
            No completed, un-billed trucking jobs between these dates.
          </div>
        ) : (
          <div className="space-y-2" data-testid="bill-cartage-list">
            {jobs.map((j) => {
              const c = cfg[j.id] || {};
              return (
                <div key={j.id} className={`border-2 p-3 ${c.selected ? "border-[#FF5F15] bg-orange-50" : "border-zinc-300"}`} data-testid={`bill-cartage-row-${j.id}`}>
                  <div className="flex items-start gap-2.5">
                    <button type="button" onClick={() => setJob(j.id, { selected: !c.selected })}
                            className={`mt-0.5 w-5 h-5 shrink-0 border-2 flex items-center justify-center ${c.selected ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-400"}`}
                            data-testid={`bill-cartage-tick-${j.id}`} aria-label="Include in invoice">
                      {c.selected && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold">{j.material || "General cartage"}
                        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-2">{j.date}{j.client_name ? ` · ${j.client_name}` : ""}{j.truck ? ` · ${j.truck.rego}` : ""}</span>
                      </div>
                      {(j.pickup_location || j.dropoff_location) && (
                        <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                          {j.pickup_location || "—"} <ArrowRight className="w-3 h-3" /> {j.dropoff_location || "—"}
                        </div>
                      )}
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                        {(j.actual_loads ?? j.loads_done) || 0} loads · {(j.actual_tonnes ?? j.tonnes_done) || 0}t · {j.hours_logged || 0}h
                        {(j.dockets || []).length > 0 && <span className="text-[#FF5F15]"> · {(j.dockets || []).length} docket{(j.dockets || []).length > 1 ? "s" : ""} attached</span>}
                      </div>
                      {c.selected && (
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Select value={c.unit} onValueChange={(u) => setJob(j.id, {
                            unit: u, qty: qtyFor(j, u),
                            rate: rates[`${(j.material || "").trim().toLowerCase()}|${u}`] ?? c.rate ?? "",
                          })}>
                            <SelectTrigger className="h-8 w-28 rounded-sm border-zinc-400 text-xs bg-white" data-testid={`bill-cartage-unit-${j.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {UNITS.map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input type="number" min="0" step="0.01" value={c.qty} onChange={(e) => setJob(j.id, { qty: e.target.value })}
                                 className="h-8 w-20 rounded-sm border-zinc-400 text-xs bg-white" data-testid={`bill-cartage-qty-${j.id}`} />
                          <span className="text-xs text-zinc-500">×</span>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-500">$</span>
                            <Input type="number" min="0" step="0.01" value={c.rate} onChange={(e) => setJob(j.id, { rate: e.target.value })}
                                   placeholder="rate" className="h-8 w-24 pl-5 rounded-sm border-zinc-400 text-xs bg-white" data-testid={`bill-cartage-rate-${j.id}`} />
                          </div>
                          <span className="text-sm font-mono font-bold text-[#FF5F15] ml-auto" data-testid={`bill-cartage-line-total-${j.id}`}>{money(lineTotal(j))}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between border-t-2 border-zinc-900 pt-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{selected.length} job{selected.length === 1 ? "" : "s"} · subtotal excl. GST</span>
          <span className="font-display font-black text-xl" data-testid="bill-cartage-subtotal">{money(subtotal)}</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm font-bold uppercase tracking-wider text-xs">Cancel</Button>
          <Button onClick={create} disabled={busy || !selected.length} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-xs" data-testid="bill-cartage-create">
            {busy ? "Creating…" : "Create invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
